import { useState, useEffect, useRef } from 'react';
import { TaskService } from '../services';
import { 
  extractTokenFromInput, 
  isValidRedeemCode, 
  processRedeemCode,
  saveToLocalStorage,
  loadFromLocalStorage,
  removeFromLocalStorage,
  saveSuccessRecord
} from '../utils';
import { STORAGE_KEYS } from '../constants/config';

export const useTaskProcessor = (addLog) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  
  // 单个任务状态
  const [accountInput, setAccountInput] = useState('');
  const [redeemCode, setRedeemCode] = useState('');
  const [extractedToken, setExtractedToken] = useState({ type: '', value: '', email: null });
  const [taskStatus, setTaskStatus] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [taskError, setTaskError] = useState(null);
  const [taskResult, setTaskResult] = useState(null);
  const [taskRetryCount, setTaskRetryCount] = useState(0);

  // 验证状态
  const [isValidatingToken, setIsValidatingToken] = useState(false);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [codeValid, setCodeValid] = useState(false);

  // 用户信息
  const [userInfo, setUserInfo] = useState(null);
  const [accountInfo, setAccountInfo] = useState(null);
  const [accessToken, setAccessToken] = useState('');

  // 渠道商信息
  const [provider, setProvider] = useState(null);
  const [providerDisplay, setProviderDisplay] = useState('');
  const [supportsOverwrite, setSupportsOverwrite] = useState(false);
  const [isOverwrite, setIsOverwrite] = useState(false);

  // 确认状态
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showRetryModal, setShowRetryModal] = useState(false);

  const taskService = useRef(new TaskService(addLog)).current;

  // 从URL获取兑换码
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const codeParam = urlParams.get('card') || urlParams.get('code') || urlParams.get('key');
    if (codeParam) {
      const formattedCode = codeParam.toUpperCase();
      setRedeemCode(formattedCode);
      addLog(`从URL读取到兑换码: ${formattedCode}`, 'info');
    }
  }, [addLog]);

  // 初始化时从本地存储恢复数据
  useEffect(() => {
    const singleTaskData = loadFromLocalStorage(STORAGE_KEYS.SINGLE_TASK);
    if (singleTaskData) {
      setAccountInput(singleTaskData.account || '');
      setRedeemCode(singleTaskData.code || '');
    }
  }, []);

  // 保存单个任务数据到本地存储
  useEffect(() => {
    const data = {
      account: accountInput,
      code: redeemCode
    };
    saveToLocalStorage(STORAGE_KEYS.SINGLE_TASK, data);
  }, [accountInput, redeemCode]);

  // 解析账号输入，提取token
  useEffect(() => {
    const tokenData = extractTokenFromInput(accountInput);
    setExtractedToken(tokenData);
    setTokenValid(false);
    
    if (tokenData.email) {
      setUserInfo({ email: tokenData.email });
      addLog(`从JSON中获取到邮箱: ${tokenData.email}`, 'info');
    }
  }, [accountInput, addLog]);

  // 解析兑换码输入 - 移除自动处理以避免循环
  useEffect(() => {
    setCodeValid(false);
    setProvider(null);
    setProviderDisplay('');
    setSupportsOverwrite(false);
    setIsOverwrite(false);
  }, [redeemCode]);

  // 验证令牌
  const validateToken = async () => {
    if (!extractedToken.value) {
      addLog('请输入有效的账号信息', 'error');
      setError('请输入有效的账号信息');
      return { valid: false };
    }

    setIsValidatingToken(true);
    try {
      const result = await taskService.validateToken(extractedToken.value, extractedToken.type);
      
      if (result.success) {
        setTokenValid(true);
        setUserInfo(result.data.user_info);
        setAccountInfo(result.data.account_info);
        setAccessToken(result.data.access_token);
        setError(null);
        return { valid: true, data: result.data };
      } else {
        setError(result.error);
        setTokenValid(false);
        return { valid: false };
      }
    } finally {
      setIsValidatingToken(false);
    }
  };

  // 验证兑换码
  const validateRedeemCode = async () => {
    if (!isValidRedeemCode(redeemCode)) {
      addLog('兑换码格式无效，必须是16-32位字母和数字', 'error');
      setError('兑换码格式无效，必须是16-32位字母和数字');
      return { valid: false };
    }

    setIsValidatingCode(true);
    try {
      const result = await taskService.validateRedeemCode(redeemCode);
      
      if (result.success) {
        const overwrite = result.supports_overwrite || false;
        setCodeValid(true);
        setProvider(result.provider);
        setProviderDisplay(result.provider_display || result.provider);
        setSupportsOverwrite(overwrite);
        setError(null);
        return { valid: true, supportsOverwrite: overwrite };
      } else {
        setError(result.error);
        setCodeValid(false);
        setProvider(null);
        setProviderDisplay('');
        setSupportsOverwrite(false);
        return { valid: false };
      }
    } finally {
      setIsValidatingCode(false);
    }
  };

  // 预检查单个任务
  const runPrecheck = async () => {
    setError(null);
    setTaskStatus(null);
    setTaskId(null);
    setTaskError(null);
    setTaskResult(null);
    setTaskRetryCount(0);
    setShowConfirmation(false);
    setIsOverwrite(false);

    const codeResult = await validateRedeemCode();
    if (!codeResult.valid) return false;

    const tokenResult = await validateToken();
    if (!tokenResult.valid) return false;

    const acctInfo = tokenResult.data.account_info;
    if (acctInfo && !acctInfo.is_free) {
      if (codeResult.supportsOverwrite) {
        setIsOverwrite(true);
        addLog(`账户已有会员(${acctInfo.expires_at}到期)，将进行覆盖充值`, 'info');
      } else {
        setError(`账户已有会员，预计于 ${acctInfo.expires_at} 到期`);
        addLog(`账户已有会员，预计于 ${acctInfo.expires_at} 到期，当前卡密不支持覆盖充值`, 'error');
        return false;
      }
    }

    setShowConfirmation(true);
    return true;
  };

  // 处理单个任务提交
  const handleSubmit = async () => {
    setShowConfirmation(false);
    setIsProcessing(true);
    setTaskStatus('processing');
    setTaskError(null);
    setTaskResult(null);
    setTaskRetryCount(0);

    const taskData = {
      access_token: accessToken,
      redeem_code: redeemCode,
      original_input: accountInput,
      email: userInfo.email,
      account_info: accountInfo,
      provider: provider,
      user_id: userInfo?.user_id || '',
      allow_overwrite: isOverwrite,
    };

    const submitResult = await taskService.submitTaskWithRetry(taskData);
    
    if (submitResult.success) {
      setTaskId(submitResult.taskId);

      if (submitResult.immediate) {
        setTaskStatus('completed');
        setTaskResult(submitResult.result || '兑换成功');
        addLog(`任务立即完成: ${submitResult.result || '兑换成功'}`, 'success');
        const successRecord = {
          email: userInfo?.email || '未知',
          code: redeemCode,
          account: userInfo?.email || redeemCode,
          time: new Date().toISOString(),
          type: 'single',
          channel: submitResult.channel,
        };
        saveSuccessRecord(successRecord);
      } else {
        const retryContext = {
          redeemCode,
          email: userInfo?.email || '',
          accountInfo: accountInfo,
          accessToken: accessToken,
          userId: userInfo?.user_id || '',
          originalInput: accountInput,
          autoRetryCount: 0,
          excludeProviders: [],
        };

        const pollResult = await taskService.pollTaskStatus(
          submitResult.taskId, 
          (status, result, newTaskId) => {
            setTaskStatus(status);
            if (newTaskId) {
              setTaskId(newTaskId);
            }
            if (result) {
              if (status === 'completed') {
                setTaskResult(result);
              } else if (status === 'failed') {
                setTaskError(result);
              }
            }
          },
          retryContext
        );

        if (pollResult.success) {
          const successRecord = {
            email: userInfo?.email || '未知',
            code: redeemCode,
            account: userInfo?.email || redeemCode,
            time: new Date().toISOString(),
            type: 'single',
            channel: submitResult.channel,
          };
          saveSuccessRecord(successRecord);
        } else if (pollResult.canManualRetry || taskRetryCount < 3) {
          setShowRetryModal(true);
        }
      }
    } else {
      setTaskStatus('failed');
      setTaskError(submitResult.error);
    }
    
    setIsProcessing(false);
  };

  // 处理重试
  const handleRetryConfirm = async () => {
    setShowRetryModal(false);
    setTaskRetryCount(prev => prev + 1);
    await handleSubmit();
  };

  // 重置表单
  const resetForm = () => {
    setAccountInput('');
    setRedeemCode('');
    setError(null);
    setTaskStatus(null);
    setTaskId(null);
    setTaskError(null);
    setTaskResult(null);
    setTaskRetryCount(0);
    setUserInfo(null);
    setAccountInfo(null);
    setAccessToken('');
    setTokenValid(false);
    setCodeValid(false);
    setProvider(null);
    setProviderDisplay('');
    setSupportsOverwrite(false);
    setIsOverwrite(false);
    setShowConfirmation(false);
    setShowRetryModal(false);
    removeFromLocalStorage(STORAGE_KEYS.SINGLE_TASK);
  };

  return {
    // 状态
    isProcessing,
    error,
    accountInput,
    redeemCode,
    extractedToken,
    taskStatus,
    taskId,
    taskError,
    taskResult,
    taskRetryCount,
    isValidatingToken,
    isValidatingCode,
    tokenValid,
    codeValid,
    userInfo,
    accountInfo,
    provider,
    providerDisplay,
    showConfirmation,
    showRetryModal,
    isOverwrite,
    
    // 设置器
    setAccountInput,
    setRedeemCode,
    setError,
    setShowConfirmation,
    setShowRetryModal,
    
    // 方法
    validateToken,
    validateRedeemCode,
    runPrecheck,
    handleSubmit,
    handleRetryConfirm,
    resetForm
  };
};